import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Table, Button, Input, Space, Tag, Popconfirm, message, Card, Row, Col, Select } from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  SearchOutlined,
  KeyOutlined,
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { adminUsersApi } from '@/api/admin-users'
import type { AdminUser } from '@/types/admin-user'
import { useAuth } from '@/hooks/useAuth'
import { getApiErrorMessage } from '@/utils/error'

const { Search } = Input

export const AdminUserList = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user: currentUser } = useAuth()
  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState<string | undefined>(undefined)

  // Fetch admin users
  const { data, isLoading } = useQuery({
    queryKey: ['admin-users', search, roleFilter],
    queryFn: () =>
      adminUsersApi.getAdminUsers({
        search: search || undefined,
        role: roleFilter,
      }),
  })

  // Delete user mutation
  const deleteMutation = useMutation({
    mutationFn: adminUsersApi.deleteAdminUser,
    onSuccess: () => {
      message.success('Admin user deleted successfully')
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
    },
    onError: (error: unknown) => {
      message.error(getApiErrorMessage(error, 'Failed to delete admin user'))
    },
  })

  // Reset password mutation
  const resetPasswordMutation = useMutation({
    mutationFn: adminUsersApi.resetPassword,
    onSuccess: () => {
      message.success('Password reset email sent successfully')
    },
    onError: (error: unknown) => {
      message.error(getApiErrorMessage(error, 'Failed to reset password'))
    },
  })

  const handleDelete = (email: string) => {
    deleteMutation.mutate(email)
  }

  const handleResetPassword = (email: string) => {
    resetPasswordMutation.mutate(email)
  }

  const roleColors: Record<string, string> = {
    ADMIN: 'red',
    SUPPORT: 'blue',
    VIEWER: 'green',
  }

  const columns: ColumnsType<AdminUser> = [
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      sorter: (a, b) => a.email.localeCompare(b.email),
    },
    {
      title: 'Name',
      key: 'name',
      render: (_, record) => `${record.given_name} ${record.family_name}`,
      sorter: (a, b) => a.given_name.localeCompare(b.given_name),
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (role: string) => <Tag color={roleColors[role]}>{role}</Tag>,
      filters: [
        { text: 'ADMIN', value: 'ADMIN' },
        { text: 'SUPPORT', value: 'SUPPORT' },
        { text: 'VIEWER', value: 'VIEWER' },
      ],
      onFilter: (value, record) => record.role === value,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const color = status === 'CONFIRMED' ? 'green' : 'orange'
        return <Tag color={color}>{status}</Tag>
      },
    },
    {
      title: 'Email Verified',
      dataIndex: 'email_verified',
      key: 'email_verified',
      render: (verified: boolean) => (
        <Tag color={verified ? 'green' : 'red'}>{verified ? 'Yes' : 'No'}</Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => {
        const isCurrentUser = currentUser?.email === record.email

        return (
          <Space size="small">
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/admin/users/${record.email}`)}
            >
              View
            </Button>
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => navigate(`/admin/users/${record.email}/edit`)}
            >
              Edit
            </Button>
            <Popconfirm
              title="Reset password"
              description="Send password reset email to this user?"
              onConfirm={() => handleResetPassword(record.email)}
              okText="Yes"
              cancelText="No"
            >
              <Button type="link" size="small" icon={<KeyOutlined />}>
                Reset Password
              </Button>
            </Popconfirm>
            {!isCurrentUser && (
              <Popconfirm
                title="Delete admin user"
                description="Are you sure you want to delete this admin user?"
                onConfirm={() => handleDelete(record.email)}
                okText="Yes"
                cancelText="No"
              >
                <Button type="link" size="small" danger icon={<DeleteOutlined />}>
                  Delete
                </Button>
              </Popconfirm>
            )}
          </Space>
        )
      },
    },
  ]

  return (
    <Card
      title="Admin Users"
      extra={
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate('/admin/users/create')}
        >
          Create Admin User
        </Button>
      }
    >
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} md={8}>
          <Search
            placeholder="Search by email"
            allowClear
            enterButton={<SearchOutlined />}
            onSearch={setSearch}
          />
        </Col>
        <Col xs={24} sm={12} md={8}>
          <Select
            style={{ width: '100%' }}
            placeholder="Filter by role"
            allowClear
            onChange={setRoleFilter}
            options={[
              { label: 'ADMIN', value: 'ADMIN' },
              { label: 'SUPPORT', value: 'SUPPORT' },
              { label: 'VIEWER', value: 'VIEWER' },
            ]}
          />
        </Col>
      </Row>

      <Table
        columns={columns}
        dataSource={data?.data.users || []}
        loading={isLoading}
        rowKey="sub"
        pagination={{
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} admin users`,
        }}
      />
    </Card>
  )
}
